import { Link, useNavigate } from "react-router-dom";
import { FormEvent, useEffect, useState } from "react";
import {
  ArrowRight,
  Copy,
  FolderKanban,
  KeyRound,
  Plus,
  Trash2,
} from "lucide-react";
import { api, type Project } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { PlatformShell } from "@/components/AppShell";
import {
  EmptyState,
  ErrorBox,
  PageHeader,
  PageLoading,
  Spinner,
} from "@/components/ui";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
export function ProjectsPage() {
  const { user, loading, signOut } = useAuth();
  const navigate = useNavigate();
  const [projects, setProjects] = useState<Project[]>([]);
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(true);
  const [name, setName] = useState("");
  const [creating, setCreating] = useState(false);
  const [starterNotes, setStarterNotes] = useState(false);
  const [createdKeys, setCreatedKeys] = useState<{
    public_key: string;
    service_key: string;
    project: Project;
  } | null>(null);
  const [copied, setCopied] = useState<string | null>(null);
  const hasProvisioningProject = projects.some((project) => project.status === "provisioning");

  useEffect(() => {
    if (loading) return;
    if (!user) {
      navigate("/login", { replace: true });
      return;
    }
    void (async () => {
      setBusy(true);
      try {
        const { data } = await api.listProjects();
        setProjects(data);
      } catch (e) {
        setError(e);
      } finally {
        setBusy(false);
      }
    })();
  }, [user, loading, navigate]);

  useEffect(() => {
    if (!user || !hasProvisioningProject) return;
    const timer = window.setInterval(() => {
      void api.listProjects().then(({ data }) => setProjects(data)).catch(setError);
    }, 2000);
    return () => window.clearInterval(timer);
  }, [user, hasProvisioningProject]);

  async function onCreate(e: FormEvent) {
    e.preventDefault();
    setCreating(true);
    setError(null);
    try {
      const shouldBootstrapNotes = starterNotes;
      const { data } = await api.createProject(name.trim());
      setCreatedKeys({
        public_key: data.public_key,
        service_key: data.service_key,
        project: data.project,
      });
      setProjects((p) => [data.project, ...p]);
      if (shouldBootstrapNotes) {
        const activeProject = await waitForProjectActive(data.project.id);
        setProjects((projects) => projects.map((project) => project.id === activeProject.id ? activeProject : project));
        setCreatedKeys((created) => created ? { ...created, project: activeProject } : created);
        await api.bootstrapProject(data.project.id, "notes");
      }
      setName("");
      setStarterNotes(false);
    } catch (err) {
      setError(err);
    } finally {
      setCreating(false);
    }
  }

  async function onDelete(id: string) {
    if (!confirm("Soft-delete this project? API keys will be revoked.")) return;
    try {
      await api.deleteProject(id);
      setProjects((p) => p.filter((x) => x.id !== id));
    } catch (err) {
      setError(err);
    }
  }

  async function copyText(label: string, value: string) {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(label);
      window.setTimeout(() => setCopied(null), 1500);
    } catch {
      /* ignore */
    }
  }

  if (loading || !user) {
    return <PageLoading label="Loading workspace…" />;
  }

  return (
    <PlatformShell email={user.email} onSignOut={signOut} operatorRole={user.operator_role}>
      <PageHeader
        eyebrow="Workspace"
        title="Projects"
        description="Provision a dedicated PostgreSQL database with bounded resources, copy API keys once, and open a project to connect clients."
        actions={
          <Badge variant="secondary" className="tabular-nums">
            {busy ? "…" : projects.length} project
            {!busy && projects.length !== 1 ? "s" : ""}
          </Badge>
        }
      />

      {error ? (
        <div className="mb-4">
          <ErrorBox error={error} />
        </div>
      ) : null}

      {createdKeys ? (
        <Card className="card-key mb-6 border-primary-strong/30">
          <CardHeader className="pb-2">
            <div className="flex flex-wrap items-start justify-between gap-2">
              <div className="flex items-start gap-3">
                <div className="flex h-9 w-9 items-center justify-center rounded-md border border-primary-strong/30 bg-white/70 text-primary-strong">
                  <KeyRound className="h-4 w-4" />
                </div>
                <div>
                  <CardTitle className="text-primary-strong">
                    Copy keys now
                  </CardTitle>
                  <CardDescription className="mt-1">
                    Full keys are shown once. After this, only prefixes appear in
                    the console.
                  </CardDescription>
                </div>
              </div>
              <Badge variant="copper">one-time</Badge>
            </div>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="text-xs text-text-muted">
              Project ·{" "}
              <span className="font-medium text-text-primary">
                {createdKeys.project.name}
              </span>
              <span className="mx-1.5 text-border-strong">·</span>
              <span className="font-mono text-text-disabled">
                {createdKeys.project.schema_name}
              </span>
            </div>
            <KeyBlock
              label="Public key"
              value={createdKeys.public_key}
              onCopy={() =>
                void copyText("public", createdKeys.public_key)
              }
              copied={copied === "public"}
            />
            <KeyBlock
              label="Service key"
              value={createdKeys.service_key}
              onCopy={() =>
                void copyText("service", createdKeys.service_key)
              }
              copied={copied === "service"}
            />
            <p className="text-xs text-primary-strong">
              Public keys are for browser clients after you configure Browser
              origins. Service keys bypass RLS and must stay on trusted servers.
            </p>
            <div className="flex flex-wrap gap-2 pt-1">
              <Button asChild>
                <Link to={`/dashboard/projects/${createdKeys.project.id}`}>
                  Open project
                  <ArrowRight className="h-4 w-4" />
                </Link>
              </Button>
              <Button
                variant="outline"
                type="button"
                onClick={() => setCreatedKeys(null)}
              >
                Dismiss
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : null}

      <Card className="project-create-card mb-6">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2">
            <Plus className="h-4 w-4 text-secondary" />
            Provision project
          </CardTitle>
          <CardDescription>
            Creates a dedicated PostgreSQL database and issues public + service keys.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form
            onSubmit={onCreate}
            className="flex flex-wrap items-end gap-3"
          >
            <div className="min-w-[14rem] flex-1 space-y-1.5">
              <Label htmlFor="project-name">Project name</Label>
              <Input
                id="project-name"
                placeholder="e.g. todos-app"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                autoComplete="off"
              />
            </div>
            <label className="flex items-center gap-2 pb-2 text-sm text-text-muted">
              <input
                type="checkbox"
                checked={starterNotes}
                onChange={(event) => setStarterNotes(event.target.checked)}
              />
              Add user-owned notes starter
            </label>
            <Button disabled={creating} type="submit">
              {creating ? "Creating…" : "Create project"}
            </Button>
          </form>
        </CardContent>
      </Card>

      {busy ? (
        <Spinner label="Loading projects…" />
      ) : projects.length === 0 ? (
        <EmptyState
          title="No projects yet"
          icon={<FolderKanban className="h-5 w-5" />}
        >
          Name a project above to provision a dedicated database and API keys.
        </EmptyState>
      ) : (
        <ul className="grid gap-3">
          {projects.map((p) => (
            <li key={p.id}>
              <Card className="project-list-card group transition-[border-color,box-shadow,background-color,transform] duration-standard ease-standard hover:-translate-y-0.5 hover:border-border-strong">
                <CardContent className="flex flex-wrap items-center justify-between gap-3 p-4 sm:p-5">
                  <div className="flex min-w-0 items-center gap-3">
                    <div className="project-list-icon" aria-hidden>
                      <FolderKanban className="h-4 w-4" />
                    </div>
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <Link
                          to={`/dashboard/projects/${p.id}`}
                          className="font-sans text-base font-semibold text-text-primary transition-colors duration-fast ease-standard group-hover:text-secondary"
                        >
                          {p.name}
                        </Link>
                        <Badge variant="secondary" className="capitalize">
                          {p.status}
                        </Badge>
                      </div>
                      <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 font-mono text-[11px] text-text-disabled">
                      <span className="text-text-muted">{p.schema_name}</span>
                      <span className="text-border-strong">·</span>
                      <span>{new Date(p.created_at).toLocaleString()}</span>
                      <span className="text-border-strong">·</span>
                      <span className="truncate">{p.id.slice(0, 8)}</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" asChild>
                      <Link to={`/dashboard/projects/${p.id}`}>
                        Open
                        <ArrowRight className="h-3.5 w-3.5" />
                      </Link>
                    </Button>
                    <Button
                      variant="destructive"
                      size="sm"
                      type="button"
                      onClick={() => void onDelete(p.id)}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                      Delete
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </li>
          ))}
        </ul>
      )}
    </PlatformShell>
  );
}

export async function waitForProjectActive(
  projectId: string,
  options: { timeoutMs?: number; pollMs?: number } = {},
): Promise<Project> {
  const timeoutMs = options.timeoutMs ?? 120_000;
  const pollMs = options.pollMs ?? 1_000;
  const deadline = Date.now() + timeoutMs;
  while (Date.now() <= deadline) {
    const { data } = await api.getProject(projectId);
    if (data.status === "active") return data;
    if (data.status === "failed" || data.status === "deleted") {
      throw new Error(`Project database provisioning ${data.status}. Open Database status for diagnostics.`);
    }
    await new Promise((resolve) => window.setTimeout(resolve, pollMs));
  }
  throw new Error("Project database provisioning timed out. Open Database status for diagnostics.");
}

function KeyBlock({
  label,
  value,
  onCopy,
  copied,
}: {
  label: string;
  value: string;
  onCopy: () => void;
  copied: boolean;
}) {
  return (
    <div className="rounded-md border border-primary-strong/20 bg-white/70">
      <div className="flex items-center justify-between gap-2 border-b border-primary-strong/15 px-3 py-1.5">
        <span className="font-mono text-[10px] uppercase tracking-label text-primary-strong">
          {label}
        </span>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-7 px-2 text-primary-strong"
          onClick={onCopy}
        >
          <Copy className="h-3.5 w-3.5" />
          {copied ? "Copied" : "Copy"}
        </Button>
      </div>
      <pre className="overflow-x-auto p-3 font-mono text-xs leading-relaxed text-text-secondary">
        {value}
      </pre>
    </div>
  );
}
